import { useUser } from "@/components/UserProvider";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { TreatmentAppointmentOutcomeBadge } from "@/components/features/matter/medical-treatments/TreatmentAppointmentOutcomeBadge";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useMedicalTreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import type { Matter, MedicalTreatmentAppointment } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getConnectionContactName } from "@/lib/utils/connections/item-contact-connections";
import dayjs from "dayjs";
import { getTreatmentAppointmentStartsAt } from "@/lib/utils/matters/medical-treatment-appointment-dates";
import { useEffect, useRef } from "react";
import { SectionAction } from "./SectionAction";

type TimelineAppointment = MedicalTreatmentAppointment & {
  providerName: string;
};

function getAppointmentDateTime(appointment: MedicalTreatmentAppointment) {
  const startsAt = getTreatmentAppointmentStartsAt(appointment);
  return startsAt.isValid() ? startsAt : null;
}

function formatAppointmentDate(appointment: MedicalTreatmentAppointment) {
  const date = getTreatmentAppointmentStartsAt(appointment);
  if (!date.isValid()) {
    return "Date unknown";
  }

  return date.year() === dayjs().year()
    ? date.format("MMM D")
    : date.format("MMM D, YYYY");
}

function getAppointmentTitle(appointment: MedicalTreatmentAppointment) {
  const types = appointment.type ?? [];
  if (types.length === 0) {
    return "Treatment appointment";
  }

  return `${types.join(", ")} appointment`;
}

function formatAppointmentTime(appointment: MedicalTreatmentAppointment) {
  if (!appointment.time) {
    return null;
  }

  const appointmentDateTime = getTreatmentAppointmentStartsAt(appointment);
  return appointmentDateTime.isValid()
    ? appointmentDateTime.format("h:mm A")
    : null;
}

export function TreatmentTimeline({
  matter,
  title = "Treatment timeline",
  embedded = false,
}: {
  matter: Matter;
  title?: string;
  embedded?: boolean;
}) {
  const { hasPagePermission } = useUser();
  const canViewTreatments = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailMedicalTreatments,
  );
  const treatments = useMedicalTreatmentViewModel(matter);
  const today = dayjs().startOf("day");
  const appointments = treatments
    .flatMap<TimelineAppointment>((treatment) => {
      const providerName =
        getConnectionContactName(
          treatment.provider?.contact ?? treatment.treatingPhysician?.contact,
        ) || "Provider not assigned";

      return treatment.appointments.map((appointment) => ({
        ...appointment,
        providerName,
      }));
    })
    .sort((a, b) => {
      const aDate = getAppointmentDateTime(a)?.valueOf();
      const bDate = getAppointmentDateTime(b)?.valueOf();

      if (aDate == null && bDate == null) return 0;
      if (aDate == null) return 1;
      if (bDate == null) return -1;
      return bDate - aDate;
    });
  const firstPastAppointmentIndex = appointments.findIndex((appointment) => {
    const appointmentDate = getAppointmentDateTime(appointment);
    return !appointmentDate || !appointmentDate.startOf("day").isAfter(today);
  });
  const todayDividerIndex =
    firstPastAppointmentIndex === -1
      ? appointments.length
      : firstPastAppointmentIndex;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const todayMarkerRef = useRef<HTMLDivElement>(null);
  const timelineKey = appointments
    .map((appointment) =>
      [appointment.id, appointment.date, appointment.time].join(":"),
    )
    .join("|");

  useEffect(() => {
    let centerFrame = 0;
    const layoutFrame = window.requestAnimationFrame(() => {
      centerFrame = window.requestAnimationFrame(() => {
        const scrollContainer = scrollContainerRef.current;
        const todayMarker = todayMarkerRef.current;
        if (!scrollContainer || !todayMarker) {
          return;
        }

        const containerBounds = scrollContainer.getBoundingClientRect();
        const markerBounds = todayMarker.getBoundingClientRect();
        const markerTop =
          scrollContainer.scrollTop + markerBounds.top - containerBounds.top;

        scrollContainer.scrollTop = Math.max(
          0,
          markerTop -
            scrollContainer.clientHeight / 2 +
            markerBounds.height / 2,
        );
      });
    });

    return () => {
      window.cancelAnimationFrame(layoutFrame);
      window.cancelAnimationFrame(centerFrame);
    };
  }, [matter.id, timelineKey]);

  return (
    <section className="min-w-0">
      <div
        className={cn(
          "mb-4 flex flex-wrap items-center justify-between gap-3",
          embedded && "border-b pb-4",
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <h3
            className={cn(
              "text-foreground text-2xl font-bold break-words",
              embedded && "text-sm font-semibold",
            )}
          >
            {title}
          </h3>
          <span className="border-border/80 text-muted-foreground inline-flex h-7 min-w-7 items-center justify-center rounded-xl border px-2 text-sm">
            {appointments.length}
          </span>
        </div>
        {canViewTreatments ? (
          <SectionAction
            to="/matters/$matterId/medical-treatments"
            params={{ matterId: matter.id }}
            label="View treatment"
          />
        ) : null}
      </div>

      <div
        ref={scrollContainerRef}
        className={cn(
          "border-border/80 bg-card rounded-[20px] border px-4 py-5 sm:px-6",
          embedded && "rounded-none border-0 px-0 py-0 sm:px-0",
          appointments.length > 0 &&
            "h-[min(65vh,32rem)] overflow-y-auto overscroll-contain",
        )}
      >
        {appointments.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No treatment appointments added yet.
          </p>
        ) : (
          <ol aria-label="Treatment appointments">
            {appointments.map((appointment, index) => {
              const appointmentDate = getAppointmentDateTime(appointment);
              const isUpcoming = Boolean(
                (appointment.outcome == null ||
                  appointment.outcome === "scheduled") &&
                  appointmentDate?.isAfter(dayjs()),
              );
              const description = appointment.description?.trim();
              const appointmentTime = formatAppointmentTime(appointment);
              const appointmentTitle = getAppointmentTitle(appointment);

              return (
                <li key={appointment.id}>
                  {index === todayDividerIndex ? (
                    <div
                      ref={todayMarkerRef}
                      className="grid grid-cols-[74px_24px_minmax(0,1fr)] items-center sm:grid-cols-[96px_28px_minmax(0,1fr)]"
                    >
                      <div />
                      <div className="bg-border h-full w-px justify-self-center" />
                      <div className="relative my-2 flex items-center">
                        <span className="bg-destructive z-10 rounded-full px-2.5 py-0.5 text-[10px] font-medium text-white">
                          Today — {today.format("MMM D")}
                        </span>
                        <span className="bg-destructive absolute right-0 left-0 h-px" />
                      </div>
                    </div>
                  ) : null}

                  <div className="grid grid-cols-[74px_24px_minmax(0,1fr)] items-stretch sm:grid-cols-[96px_28px_minmax(0,1fr)]">
                    <time
                      dateTime={appointment.date}
                      className="text-muted-foreground self-start pt-4 pr-2 text-right text-xs sm:pr-3"
                    >
                      {formatAppointmentDate(appointment)}
                    </time>
                    <div className="relative flex justify-center">
                      <span className="bg-border absolute inset-y-0 w-px" />
                      <span
                        className={cn(
                          "relative mt-[18px] h-3 w-3 rounded-full bg-emerald-500 ring-4 ring-emerald-500/15",
                          !isUpcoming && "h-2.5 w-2.5 ring-0",
                        )}
                      />
                    </div>
                    <div
                      className={cn(
                        "mb-3 min-w-0 px-3.5 py-3",
                        isUpcoming &&
                          "rounded-xl border border-emerald-300 bg-emerald-50/70 dark:border-emerald-500/40 dark:bg-emerald-500/10",
                      )}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <TooltipWrapper message={appointmentTitle}>
                          <p className="text-foreground line-clamp-2 min-w-0 flex-1 text-sm font-semibold">
                            {appointmentTitle}
                          </p>
                        </TooltipWrapper>
                        <TreatmentAppointmentOutcomeBadge
                          appointment={appointment}
                        />
                      </div>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {appointment.providerName}
                        {appointmentTime ? ` · ${appointmentTime}` : ""}
                      </p>
                      {description ? (
                        <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                          {description}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}

            {todayDividerIndex === appointments.length ? (
              <li>
                <div
                  ref={todayMarkerRef}
                  className="grid grid-cols-[74px_24px_minmax(0,1fr)] items-center sm:grid-cols-[96px_28px_minmax(0,1fr)]"
                >
                  <div />
                  <div className="bg-border h-full w-px justify-self-center" />
                  <div className="relative mt-[-4px] flex items-center">
                    <span className="bg-destructive z-10 rounded-full px-2.5 py-0.5 text-[10px] font-medium text-white">
                      Today — {today.format("MMM D")}
                    </span>
                    <span className="bg-destructive absolute right-0 left-0 h-px" />
                  </div>
                </div>
              </li>
            ) : null}
          </ol>
        )}
      </div>
    </section>
  );
}
